import React from 'react';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { ExpediaIcon } from '@assets/icons';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { usePricingByJoyPlaceIdFragment } from '@shared/hooks/usePricingByPropertyIds';

export const MatchedHotelPrice = ({ joyPlaceId }: { joyPlaceId: string }) => {
  const { t } = useTranslation('websiteEditor');
  const hotelTrans = t('customPage', 'matchedHotelDialog');
  const { price, strikeOutPrice, loading } = usePricingByJoyPlaceIdFragment(joyPlaceId);

  const expediaCompareTo = t('expediaCompareTo')({
    ExpediaCompareTo: <ExpediaIcon />,
  });

  return (
    <SkeletonGroup
      isReady={!loading}
      placeholder={
        <>
          <Divider marginTop={6} />
          <Flex
            gap={2}
            paddingY={pxToRem(10)}
            justifyContent="space-between"
            alignItems={'flex-start'}
          >
            <Skeleton
              placeholder={
                <Box
                  width={'8rem'}
                  typographyVariant={'body1'}
                >
                  &nbsp;
                </Box>
              }
            />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
            >
              <Skeleton
                placeholder={
                  <Box
                    width={'3.5rem'}
                    typographyVariant={'hed2'}
                  >
                    &nbsp;
                  </Box>
                }
              />
            </Box>
          </Flex>
          <Divider marginTop={2} />
          <Flex
            gap={2}
            paddingTop={pxToRem(10)}
            justifyContent="space-between"
            alignItems={'flex-start'}
          >
            <Skeleton
              placeholder={
                <Box
                  width={'8rem'}
                  typographyVariant={'body1'}
                >
                  &nbsp;
                </Box>
              }
            />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
            >
              <Skeleton
                placeholder={
                  <Box
                    width={'3.5rem'}
                    typographyVariant={'hed2'}
                  >
                    &nbsp;
                  </Box>
                }
              />
            </Box>
          </Flex>
        </>
      }
    >
      {price && (
        <>
          <Divider marginTop={6} />
          <Flex
            alignItems="center"
            paddingY={pxToRem(10)}
            justifyContent="space-between"
          >
            <JoyLogo
              width={43}
              height={24}
            />
            <Flex
              gap={3}
              alignItems="center"
            >
              <TextV2 typographyVariant="body1">{hotelTrans.startingAt()}</TextV2>
              <TextV2
                typographyVariant="hed6"
                fontWeight={'semibold'}
              >
                {price}
              </TextV2>
            </Flex>
          </Flex>
          {strikeOutPrice && (
            <>
              <Divider marginTop={2} />
              <Flex
                alignItems="end"
                paddingTop={pxToRem(10)}
                justifyContent="space-between"
              >
                <TextV2
                  typographyVariant="body1"
                  display={{ _: 'none', sm2: 'flex' }}
                  alignItems="center"
                  gap={1}
                  marginRight={2}
                >
                  {expediaCompareTo}
                </TextV2>
                <TextV2
                  textDecoration={'line-through'}
                  color={'mono14'}
                >
                  {strikeOutPrice}
                </TextV2>
              </Flex>
            </>
          )}
        </>
      )}
    </SkeletonGroup>
  );
};
